import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { IInvoice, IInvoiceReceipt } from '../../types';
import { getInvoiceReceipts, getInvoicesBySaleOrder } from '../../services';
import { IReceipt } from 'features/salesOrder/types';
import { getReceiptsForOrder } from 'features/salesOrder/services';
import { IPassengerDetail } from 'features/passengers/types';
import { getPassengerDetails } from 'features/passengers/services';

/**
 * Cache de facturas y anticipos libres de la orden, a nivel de pantalla.
 * Se dispara al montar (sin bloquear el render de la orden) y lo comparten
 * el box de indicadores del home y la pestaña de Facturas — navegar a la
 * sección no vuelve a disparar los requests.
 */

interface SaleOrderInvoicingContextValue {
	invoices: IInvoice[];
	invoicesLoaded: boolean;
	invoicesError: string | null;
	/** Todos los recibos (cobros) de la orden, vinculados o no. */
	receipts: IInvoiceReceipt[];
	/** Anticipos (recibos) sin vincular a ninguna factura. */
	freeReceipts: IInvoiceReceipt[];
	receiptsLoaded: boolean;
	/** Recibos completos (tarjeta, aerolínea, banco, auth, obs) de /saleorders/{id}/receipts. */
	orderReceipts: IReceipt[];
	orderReceiptsLoaded: boolean;
	orderReceiptsError: string | null;
	/** Detail completo de los pasajeros facturables (billing settings + saldos). */
	invoiceableClients: IPassengerDetail[];
	invoiceableClientsLoaded: boolean;
	refreshInvoices: () => Promise<void>;
	refreshReceipts: () => Promise<void>;
	refreshOrderReceipts: () => Promise<void>;
}

const SaleOrderInvoicingContext = createContext<SaleOrderInvoicingContextValue | null>(null);

interface SaleOrderInvoicingProviderProps {
	saleOrderId: string;
	/**
	 * Agencia resuelta de la orden. Es la que el interceptor usa para elegir la
	 * API key: sin ella la request sale con la key de la sesión, que al entrar
	 * por un link a una orden de otro país todavía no cambió.
	 *
	 * Mientras llega undefined no se pide nada. La orden se resuelve primero
	 * (getSalesOrderId sí manda la agencia explícita) y recién ahí sabemos cuál
	 * es: disparar antes garantizaba el error en la primera carga, que se
	 * arreglaba solo al recargar la página.
	 */
	agencyId?: number;
	/** keyIds de los pasajeros facturables de la orden (para traer sus billing settings). */
	invoiceablePassengerIds?: string[];
	children: React.ReactNode;
}

export const SaleOrderInvoicingProvider = ({
	saleOrderId,
	agencyId,
	invoiceablePassengerIds,
	children,
}: SaleOrderInvoicingProviderProps): JSX.Element => {
	const [invoices, setInvoices] = useState<IInvoice[]>([]);
	const [invoicesLoaded, setInvoicesLoaded] = useState(false);
	const [invoicesError, setInvoicesError] = useState<string | null>(null);

	const [receipts, setReceipts] = useState<IInvoiceReceipt[]>([]);
	const [receiptsLoaded, setReceiptsLoaded] = useState(false);

	const refreshInvoices = useCallback(async (): Promise<void> => {
		if (!saleOrderId || agencyId === undefined) return;

		try {
			setInvoicesError(null);
			const data = await getInvoicesBySaleOrder(saleOrderId, { agencyId });
			setInvoices(data);
		} catch {
			setInvoicesError('No se pudieron cargar las facturas de la orden.');
			setInvoices([]);
		} finally {
			setInvoicesLoaded(true);
		}
	}, [saleOrderId, agencyId]);

	const refreshReceipts = useCallback(async (): Promise<void> => {
		if (!saleOrderId || agencyId === undefined) return;

		try {
			const data = await getInvoiceReceipts({ saleOrderId }, { agencyId });
			setReceipts(data);
		} catch {
			setReceipts([]);
		} finally {
			setReceiptsLoaded(true);
		}
	}, [saleOrderId, agencyId]);

	const freeReceipts = useMemo(() => receipts.filter((r) => !r.invoiceId), [receipts]);

	const [orderReceipts, setOrderReceipts] = useState<IReceipt[]>([]);
	const [orderReceiptsLoaded, setOrderReceiptsLoaded] = useState(false);
	const [orderReceiptsError, setOrderReceiptsError] = useState<string | null>(null);

	const refreshOrderReceipts = useCallback(async (): Promise<void> => {
		if (!saleOrderId || agencyId === undefined) return;

		try {
			setOrderReceiptsError(null);
			const response = await getReceiptsForOrder(saleOrderId, { agencyId });
			setOrderReceipts(response.data);
		} catch {
			setOrderReceiptsError('No se pudieron cargar los cobros.');
			setOrderReceipts([]);
		} finally {
			setOrderReceiptsLoaded(true);
		}
	}, [saleOrderId, agencyId]);

	useEffect(() => {
		refreshInvoices();
		refreshReceipts();
		refreshOrderReceipts();
	}, [refreshInvoices, refreshReceipts, refreshOrderReceipts]);

	const [invoiceableClients, setInvoiceableClients] = useState<IPassengerDetail[]>([]);
	const [invoiceableClientsLoaded, setInvoiceableClientsLoaded] = useState(false);

	/** Clave estable para no re-fetchear cuando el array llega con nueva identidad. */
	const invoiceableIdsKey = (invoiceablePassengerIds ?? []).join(',');

	useEffect(() => {
		if (!invoiceableIdsKey) {
			setInvoiceableClients([]);
			return;
		}

		let cancelled = false;

		Promise.all(
			invoiceableIdsKey.split(',').map((id) =>
				getPassengerDetails(id)
					.then((response) => response.data)
					.catch(() => null),
			),
		).then((results) => {
			if (cancelled) return;
			setInvoiceableClients(results.filter((r): r is IPassengerDetail => r !== null));
			setInvoiceableClientsLoaded(true);
		});

		return () => {
			cancelled = true;
		};
	}, [invoiceableIdsKey]);

	const value = useMemo(
		() => ({
			invoices,
			invoicesLoaded,
			invoicesError,
			receipts,
			freeReceipts,
			receiptsLoaded,
			orderReceipts,
			orderReceiptsLoaded,
			orderReceiptsError,
			invoiceableClients,
			invoiceableClientsLoaded,
			refreshInvoices,
			refreshReceipts,
			refreshOrderReceipts,
		}),
		[
			invoices,
			invoicesLoaded,
			invoicesError,
			receipts,
			freeReceipts,
			receiptsLoaded,
			orderReceipts,
			orderReceiptsLoaded,
			orderReceiptsError,
			invoiceableClients,
			invoiceableClientsLoaded,
			refreshInvoices,
			refreshReceipts,
			refreshOrderReceipts,
		],
	);

	return <SaleOrderInvoicingContext.Provider value={value}>{children}</SaleOrderInvoicingContext.Provider>;
};

/** Variante opcional: devuelve null fuera del provider (ej. listado global de recibos). */
export const useSaleOrderInvoicingOptional = (): SaleOrderInvoicingContextValue | null =>
	useContext(SaleOrderInvoicingContext);

export const useSaleOrderInvoicing = (): SaleOrderInvoicingContextValue => {
	const context = useContext(SaleOrderInvoicingContext);
	if (!context) {
		throw new Error('useSaleOrderInvoicing debe usarse dentro de SaleOrderInvoicingProvider');
	}
	return context;
};
